import { styled as muiStyled } from '@mui/material/styles'
import Layout from '@/layouts/index'
import Page from '@/components/Page'
import pageService, {
  GlobalSetting,
  SeoMetadata,
  getAvailablePrompts,
  settingService,
} from '@/services/page'
import pathService from '@/services/path'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { APPS } from '@/appConfig'
import type { ParsedUrlQuery } from 'querystring'
import styled from 'styled-components'
import {
  useTheme,
  Autocomplete,
  Box,
  Button,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Container,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Chip,
} from '@mui/material'
import LoadingScreen from '@/components/LoadingScreen'
import useAuth from '@/hooks/useAuth'
import {
  PromptInterfaceFragment,
  PromptSupportingLanguageMap,
} from '@/generated/graphql.prompts.node'
import palette from '@/theme/palette'
import { LoadingButton } from '@mui/lab'
import { find } from 'lodash'
import { useEffect, useState } from 'react'
import { $enum } from 'ts-enum-util'
import { useVisitorGeneratePromptQuery } from '@/generated/graphql'
import { RootStyle } from '@/sections/landing/Shares'
import Iconify from '@/components/Iconify'
import {
  PlanType,
  UseCase,
  currentConcurrentCountVar,
  getConcurrentCountByPlanType,
  languageTransformer,
  shouldDisableMagicButton,
  urlQueryParams,
} from '@/services/prompts/utils'
import { useRouter } from 'next/router'
import CopyClipboard from '@/components/CopyClipboard'
import { useReactiveVar } from '@apollo/client'
import moment from 'moment'
import { SHARED_PROMPT_HEADER_FOR_CUSTOM_PROMPT_ENGINEERING } from '@/utils/constants'

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
`

interface PathProps extends ParsedUrlQuery {
  appId: string
}

type PromptType = PromptInterfaceFragment & { isCustom?: boolean }

interface IndexProps {
  title?: string
  globalSetting?: GlobalSetting
  seoMetadataItemsCollection?: SeoMetadata[]
  prompts: PromptType[]
}

const ContentStyle = muiStyled('div')(({ theme }) => ({
  overflow: 'hidden',
  position: 'relative',
}))

PromptsPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout variant="main">{page}</Layout>
}

const TitleContainer = styled('div')`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: 4px;
  align-items: center;
  @media (max-width: 768px) {
    grid-template-columns: repeat(1, 1fr);
  }
`

interface ResultRowProps {
  key: string
  promptId: string
  language: PromptSupportingLanguageMap
  prompt: string
  promptInterface: PromptType
  displayIndex: number
}
const ResultRow = (props: ResultRowProps) => {
  const currentConcurrentCountData = useReactiveVar(currentConcurrentCountVar)
  const [textInput, setTextInput] = useState('')
  const {
    loading: visitorGeneratePromptQueryDataLoading,
    error: visitorGeneratePromptQueryError,
    refetch: visitorGeneratePromptQueryRefetch,
  } = useVisitorGeneratePromptQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      promptId: props.promptId,
      language: props.language,
      prompt: props.prompt,
    },
    context: props.promptInterface.isCustom
      ? {
          headers: {
            [SHARED_PROMPT_HEADER_FOR_CUSTOM_PROMPT_ENGINEERING]: props.promptId,
          },
        }
      : {},
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setTextInput(data.generatePrompt.createResponse?.choices[0].text ?? '-')
      currentConcurrentCountVar(currentConcurrentCountData - 1)
    },
    onError: () => {
      currentConcurrentCountVar(currentConcurrentCountData - 1)
    },
  })

  useEffect(() => {
    currentConcurrentCountVar(currentConcurrentCountData + 1)
  }, [])

  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<Iconify icon={'eva:arrow-ios-downward-fill'} width={20} height={20} />}
        aria-controls="panel2a-content"
        id="panel2a-header"
      >
        <TitleContainer>
          <Typography>
            Display:
            <Chip size="small" label={props.displayIndex} sx={{ m: 0.5 }} />
          </Typography>
          <Typography>
            Language: <Chip size="small" label={props.language} sx={{ m: 0.5 }} />
          </Typography>
          <Typography>
            Prompt:
            <br />
            <Typography color={palette.light.grey[500]} variant="caption">
              {props.prompt}
            </Typography>
          </Typography>
          <Typography>
            Purpose:
            <br />
            <Typography color={palette.light.grey[500]} variant="caption">
              {props.promptInterface.promptTeaser}
            </Typography>
          </Typography>

          {visitorGeneratePromptQueryDataLoading && <CircularProgress sx={{ mt: 1 }} />}
        </TitleContainer>
      </AccordionSummary>
      <AccordionDetails>
        {!visitorGeneratePromptQueryDataLoading && textInput && (
          <div>
            <CopyClipboard
              onShareInputValueChange={(val) => {
                setTextInput(val)
              }}
              shareInputValue={textInput}
            />
            <br />
            <Typography
              style={{ whiteSpace: 'pre-line' }}
              variant="body1"
              sx={{ mt: 1, p: 1, background: palette.light.grey[700] }}
            >
              {textInput}
            </Typography>
          </div>
        )}
        {visitorGeneratePromptQueryError && (
          <Button
            variant="outlined"
            onClick={() => {
              visitorGeneratePromptQueryRefetch()
            }}
          >
            Try it one more time :D
          </Button>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

export default function PromptsPage({
  title,
  prompts,
  globalSetting,
  seoMetadataItemsCollection,
}: IndexProps) {
  const currentConcurrentCountData = useReactiveVar(currentConcurrentCountVar)
  const router = useRouter()
  const searchString: urlQueryParams = {
    language:
      (router.query.language as PromptSupportingLanguageMap | undefined) ??
      PromptSupportingLanguageMap.English,
    useCase: (router.query.useCase as UseCase | undefined) ?? UseCase.text,
    prompt: (router.query.prompt as string | undefined) ?? '',
  }
  const theme = useTheme()
  const [activeStep, setActiveStep] = useState(1)
  const [selectedPromptId, setSelectedPromptId] = useState<string>('')
  const { error, isLoading, user, login } = useAuth()
  const [textInput, setTextInput] = useState(searchString.prompt)
  const [selectLanguage, setSelectLanguage] = useState<PromptSupportingLanguageMap>(
    searchString.language
  )
  const [activeUseCase, setActiveUseCase] = useState<UseCase>(searchString.useCase)
  const [activePlan, setActivePlan] = useState<PlanType>(PlanType.free)
  const [results, setResults] = useState<Omit<ResultRowProps, 'displayIndex'>[]>([])
  const allPrompts: PromptType[] = [
    ...(prompts ?? []),
    ...(globalSetting?.promptEngineeringsCollection.items ?? [])
      .filter((r) => r.purpose === 'custom')
      .map((item) => ({
        id: item.sys?.id ?? '-',
        promptHint: item.promptHint ?? '-',
        promptTeaser: item.teaser ?? '-',
        isCustom: true,
      })),
  ]
  useEffect(() => {
    setSelectedPromptId(allPrompts?.[0].id ?? '')
    return () => {
      currentConcurrentCountVar(0)
    }
  }, [])

  if (isLoading) return <LoadingScreen companyColor={globalSetting?.companyColor} />
  if (error)
    return (
      <StyledContainer>
        <div>{error.message}</div>
      </StyledContainer>
    )

  const selectedPrompt = find(allPrompts, { id: selectedPromptId })

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleReset = () => {
    setActiveStep(0)
  }

  let renderComponent = <div />

  const groupButtons = (
    <>
      {$enum(UseCase)
        .getValues()
        .map((useCase) => (
          <Button
            variant={activeUseCase === useCase ? 'contained' : 'outlined'}
            onClick={() => setActiveUseCase(useCase)}
            key={useCase}
            sx={{ mt: 1, mr: 1 }}
            disabled={useCase === UseCase.video || useCase === UseCase.image}
          >
            {useCase} {(useCase === UseCase.video || useCase === UseCase.image) && '(coming soon)'}
          </Button>
        ))}
    </>
  )

  switch (activeStep) {
    case 0: {
      renderComponent = <div>{groupButtons}</div>
      break
    }
    case 1: {
      renderComponent = (
        <div>
          <Autocomplete
            disablePortal
            id="language"
            value={languageTransformer(selectLanguage)}
            options={$enum(PromptSupportingLanguageMap)
              .getValues()
              .map((language) => languageTransformer(language))}
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label="Output Language"
                helperText={`Currently Supporting ${
                  $enum(PromptSupportingLanguageMap).getValues().length
                } languages`}
              />
            )}
            onChange={(_event, values) => {
              setSelectLanguage(
                values?.id ?? (PromptSupportingLanguageMap.English as PromptSupportingLanguageMap)
              )
            }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <br />
          <Autocomplete
            disablePortal
            id="combo-box-demo"
            getOptionLabel={(option) => option.label}
            options={allPrompts.map((prompt) => ({
              ...prompt,
              label: prompt.promptTeaser,
              id: prompt.id,
              value: prompt.id,
            }))}
            fullWidth
            value={{
              ...selectedPrompt,
              label: selectedPrompt?.promptTeaser ?? '',
              id: selectedPrompt?.id ?? '',
              value: selectedPrompt?.id ?? '',
            }}
            renderInput={(params) => (
              <TextField {...params} label="Use Case" helperText={selectedPrompt?.promptTeaser} />
            )}
            onChange={(_event, values) => {
              setSelectedPromptId(values?.id ?? '')
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <br />
          <TextField
            label="Prompt"
            helperText={selectedPrompt?.promptHint}
            value={textInput}
            onChange={(e) => {
              setTextInput(e.target.value)
            }}
            fullWidth
          />
          <br />
          <br />
          <LoadingButton
            disabled={
              textInput === '' ||
              selectedPromptId === '' ||
              shouldDisableMagicButton({
                planType: activePlan,
                currentConcurrentCount: currentConcurrentCountData,
              })
            }
            variant="contained"
            fullWidth
            onClick={() => {
              setResults((results) => [
                {
                  key: moment().unix().toString(),
                  promptId: selectedPromptId,
                  language: selectLanguage,
                  prompt: textInput,
                  promptInterface: selectedPrompt as PromptInterfaceFragment,
                },
                ...results,
              ])
            }}
          >
            Magic, please!
          </LoadingButton>
          <Typography variant="caption" color={palette.light.grey[500]}>
            Your current plan is "{activePlan}", your remaining concurrent tasks is{' '}
            {getConcurrentCountByPlanType(activePlan) - currentConcurrentCountData}, and your max
            concurrent tasks is {getConcurrentCountByPlanType(activePlan)}.
          </Typography>
          <br />
          <br />
          <Typography variant="h6">Results</Typography>
          {results.map((result, resultIndex) => {
            const displayIndex = results.length - resultIndex
            return (
              <ResultRow
                displayIndex={displayIndex}
                key={result.key}
                promptId={result.promptId}
                language={result.language}
                prompt={result.prompt}
                promptInterface={result.promptInterface}
              />
            )
          })}

          <br />
          <br />
        </div>
      )
      break
    }
    case 3: {
      renderComponent = <div />
      break
    }
    default:
      break
  }

  const steps = [
    {
      label: 'Pick 1 use case',
      description: `For each ad campaign that you create, you can control how much
                you're willing to spend on clicks and conversions, which networks
                and geographical locations you want your ads to show on, and more.`,
    },
    {
      label: 'Pick 1 option',
      description: 'An ad group contains one or more ads which target a shared set of keywords.',
    },
    {
      label: 'Start to make money via different ways',
      description: `Try out different ad text to see what brings in the most customers,
                and learn how to enhance your ads using features like ad extensions.
                If you run into any problems with your ads, find out how to tell if
                they're running and how to resolve approval issues.`,
    },
  ]

  return (
    <Page title={title ?? ''} metadataItems={seoMetadataItemsCollection}>
      <RootStyle theme={theme} backgroundColor="">
        <Container>
          <ContentStyle>
            <Box>
              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((step, index) => (
                  <Step key={step.label}>
                    <StepLabel
                      optional={
                        activeStep !== 0 && index === 0 ? (
                          <>{groupButtons}</>
                        ) : index === 2 ? (
                          <Typography variant="caption">Premium Only</Typography>
                        ) : null
                      }
                    >
                      {step.label}
                    </StepLabel>
                    <StepContent>
                      <Typography color={palette.light.grey[600]}>{step.description}</Typography>
                      <br />
                      {renderComponent}
                      <br />
                      <Box sx={{ mb: 2 }}>
                        <div>
                          <Button
                            disabled={activeStep >= 1}
                            variant="outlined"
                            onClick={handleNext}
                            sx={{ mt: 1, mr: 1 }}
                          >
                            {index === steps.length - 1 ? 'Finish' : 'Continue'}
                          </Button>
                          <Button
                            variant="outlined"
                            disabled={index === 0}
                            onClick={handleBack}
                            sx={{ mt: 1, mr: 1 }}
                          >
                            Back
                          </Button>
                        </div>
                      </Box>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
              {activeStep === steps.length && (
                <Paper square elevation={0} sx={{ p: 3 }}>
                  <Typography>All steps completed - you&apos;re finished</Typography>
                  <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
                    Reset
                  </Button>
                </Paper>
              )}
            </Box>
          </ContentStyle>
        </Container>
      </RootStyle>
    </Page>
  )
}

export const getStaticPaths: GetStaticPaths<PathProps> = async () => {
  const { getDefaultPaths } = pathService()
  const paths = APPS.flatMap((app) => getDefaultPaths(app.id))

  return {
    paths,
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps<IndexProps, PathProps> = async ({ params, locale }) => {
  if (!params) throw new Error('No path parameters found')

  const { appId } = params
  const { getPage } = pageService(appId)
  const { getGlobalSetting } = settingService(appId)
  const [{ title, seoMetadataItemsCollection }, globalSetting, data = []] = await Promise.all([
    getPage('/', { locale }),
    getGlobalSetting({ locale }),
    getAvailablePrompts(appId),
  ])

  return {
    props: {
      prompts: data,
      title: title ?? '',
      globalSetting,
      seoMetadataItemsCollection,
    },
    revalidate: 3600,
  }
}
